import {useCallback, useEffect, useRef} from 'react';

import {getDatadogLogs} from './datadogLogs';
import {getDatadogRum} from './datadogRum';

const UNINITIALIZED_FEATURE_FLAG_VALUE = Symbol('uninitialized-feature-flag');

// Keep one "last seen" value per flag name so repeated usage across multiple
// components/hooks does not emit duplicate evaluations.
const lastLoggedFeatureFlagValues = new Map<string, unknown>();

/**
 * Non-hook: emits a single `addFeatureFlagEvaluation` to Datadog RUM with
 * the global per-flag-name dedupe. Resolves the RUM/Logs singletons internally
 * (via `getDatadogRum` / `getDatadogLogs`) so callers don't thread them through.
 * Prefer this inside store-init `useEffect`s so panel-level flag readers don't
 * each pay the hook-chain cost (`useCallback` + `useRef` + `useEffect`) on every
 * render.
 *
 * Docs:
 * - https://docs.datadoghq.com/real_user_monitoring/feature_flag_tracking/setup/?tab=browser
 * - https://datadoghq.dev/browser-sdk/interfaces/_datadog_browser-rum.DatadogRum.html#addfeatureflagevaluation
 */
export function logFeatureFlagEvaluation(key: string, value: unknown): void {
  const flagName = repairFeatureFlagName(key);
  if (
    lastLoggedFeatureFlagValues.has(flagName) &&
    Object.is(lastLoggedFeatureFlagValues.get(flagName), value)
  ) {
    return;
  }
  lastLoggedFeatureFlagValues.set(flagName, value);
  getDatadogRum().addFeatureFlagEvaluation(flagName, value);
  getDatadogLogs().setUserProperty(`features.${flagName}`, value);
}

/**
 * React hook that returns a callback to log feature flag evaluations to Datadog.
 * The callback logs the feature flag to RUM with the repaired flag name.
 *
 * @param key - The feature flag key
 * @returns A callback function to log the feature flag value
 */
export function useDatadogFeatureFlag(key: string) {
  return useCallback(
    (value: unknown) => {
      logFeatureFlagEvaluation(key, value);
    },
    [key]
  );
}

/**
 * React hook that logs feature flag evaluations when their values change.
 * This avoids render-path side effects and duplicate evaluations for unchanged values.
 *
 * @param key - The feature flag key
 * @param value - The current evaluated value of the feature flag
 */
export function useDatadogFeatureFlagEvaluation(key: string, value: unknown) {
  const logFeatureFlag = useDatadogFeatureFlag(key);
  const lastValueRef = useRef<unknown>(UNINITIALIZED_FEATURE_FLAG_VALUE);

  useEffect(() => {
    // React best practice: side effects belong in effects, not render.
    // This guard avoids repeated logs for unchanged values within the same
    // mounted hook instance. A second dedupe layer in useDatadogFeatureFlag
    // protects against duplicate emissions across different components.
    // Docs:
    // - https://react.dev/learn/synchronizing-with-effects
    // - https://react.dev/reference/rules/components-and-hooks-must-be-pure
    if (Object.is(lastValueRef.current, value)) {
      return;
    }
    lastValueRef.current = value;
    logFeatureFlag(value);
  }, [logFeatureFlag, value]);
}

/**
 * Datadog doesn't like feature flags with `.` in the name. If you log a
 * feature with `.` in the name, it gets rolled up with all the other flags
 * under the same prefix, and sets them all to the string `object Object`. To
 * avoid that, we fixup the feature flag name so that DD doesn't interpret it
 * as an object literal path.
 *
 * @param name - The feature flag name to repair
 * @returns The repaired feature flag name with dots replaced by double underscores
 */
function repairFeatureFlagName(name: string): string {
  return name.replace(/\./g, '__');
}
